import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';

import SearchBoxDateAirport from './SearchBoxDateAirport';
import PlanePlan from './PlanePlan';
import SearchBoxFacility from '../organisms/SearchBoxFacility';
import SortList from '../molecules/SortList';
import DataUpdateTimeDisplay from '../molecules/DataUpdateTimeDisplay';
import SearchResultFacility from './SearchResultFacility';
import PlanDetail from '../pages/PlanDetail';
import {
  fetchHotels,
  setSortOrder,
  setStayNights,
} from '../../modules/hotel';
import {
  fetchPendingPlan,
  handleAddPlan,
  handleDeletePlan,
  handleUpdateCheckin,
  handleUpdateQuestion,
  removePendingPlan,
} from '../../modules/planDetail';
import { useSearchFlightSync } from '../../modules/hooks/searchFlight';
import { makeHotelsInput, makePlanInput } from '../../utils/input';
import SubscriptionTableItem from '../molecules/SubscriptionTableItem';
import { getDaysToNextPlan } from '../../utils/plans';
import { scrollToTop } from '../../utils/scroll';
import { SHOWING_HOTEL_LIMIT } from '../../constants';

const selector = (state) => ({ planDetail: state.planDetail });

const SubscriptionTable = (props) => {
  const {
    editingFlights,
    editingAccommodation,
    toggleEditingFlights,
    toggleEditingAccommodation,
    tripTotalDays,
    schedule,
    plans,
    questionAndAnswers,
    onQuestionAnswerChange,
    maxStayNights,
    setMaxStayNights,
    planStartDate,
    setPlanStartDate,
    planEndDate,
    setPlanIndex,
    planIndex,
    planOptions,
  } = props;

  const {
    dispatch, flight, hotel, handleFlightSearch,
  } = useSearchFlightSync(false, true, false);

  const { planDetail } = useSelector(selector);
  const {
    flightsInput,
    selectedOutboundFlight,
    selectedInboundFlight,
    outboundFlights,
    inboundFlights,
    searchedFlightsInput,
    selectedOutboundDate,
  } = flight;
  const {
    hotelsInput,
    paginatorInfo,
    hotels,
    searchedHotelsInput,
  } = hotel;
  const { plans: selectedPlans, pendingPlan } = planDetail;

  const [hotelIdsWithExpandPlanList, setHotelIdsWithExpandPlanList] = useState([]);

  useEffect(() => {
    if (!editingAccommodation) {
      dispatch(removePendingPlan());
    }
  }, [editingAccommodation]);

  if (editingFlights) {
    const minDateInbound = moment(planEndDate).add(1, 'days').format('YYYY-MM-DD');

    return (
      <React.Fragment>
        <div className="searchBoxDateAirportWrapper">
          <SearchBoxDateAirport
            affiliate
            disableCalendarOutbound
            disableCalendarInbound
            disablePeopleSelect
            disableOutboundDate
            handleSearch={handleFlightSearch}
            calenderMinDateInbound={minDateInbound}
          />
        </div>
        <div className="planePlanWrapper">
          <PlanePlan
            handleFlightSearch={handleFlightSearch}
            hideAirlineTab={true}
          />
        </div>
      </React.Fragment>
    );
  }

  const handlePlanSelect = (plan) => {
    const { stayNights } = hotelsInput;
    dispatch(handleAddPlan(plan, planStartDate, stayNights, searchedHotelsInput, searchedFlightsInput));

    toggleEditingAccommodation();
    setHotelIdsWithExpandPlanList([]);
  };

  if (editingAccommodation) {
    if (pendingPlan) {
      const handlePlanListLink = () => {
        dispatch(removePendingPlan());
        scrollToTop(false);
        setHotelIdsWithExpandPlanList([]);
      };

      return (
        <PlanDetail
          handlePlanSelect={handlePlanSelect}
          selectedPlan={pendingPlan}
          handlePlanListLink={handlePlanListLink}
          handlePlanListLinkBottom={handlePlanListLink}
          editingAccommodation={editingAccommodation}
        />
      );
    }

    const validOutbound = !!(outboundFlights && outboundFlights.length);
    const validInbound = !!(inboundFlights && inboundFlights.length);
    const showSortList = !!(validOutbound && validInbound && hotels && hotels.length);
    const dataUpdateTime = !!(hotels && hotels.length) && hotels[0].dataUpdateTime;

    const tripStartDate = moment(selectedOutboundDate);
    const formatedTripStartDate = tripStartDate.clone().format('YYYY-MM-DD');

    const _hotelsInput = planIndex === 1 ? {
      ...hotelsInput,
      hotelCode: selectedPlans.find(p => p.startDate === planStartDate).plan.hotel.id,
    } : {
      ...hotelsInput,
      provider: selectedPlans.find(p => p.startDate === formatedTripStartDate).plan.provider,
      planPerHotel: "20", // プラン追加時はplanPerHotelを20に設定
    };

    return (
      <React.Fragment>
        <div className="searchBoxFacilityWrapper">
          <SearchBoxFacility
            hideRoom
            hideDestination={planIndex === 1}
            handleSearch={() => {
              dispatch(
                fetchHotels(
                  makeHotelsInput(_hotelsInput, searchedFlightsInput, planStartDate),
                  SHOWING_HOTEL_LIMIT,
                  1
                )
              );

              setHotelIdsWithExpandPlanList([]);
            }}
            maxStayNights={maxStayNights}
            hideSearchCondition={planIndex !== 1}
            excludeLanding={planIndex !== 1}
            limitedOptions={editingAccommodation}
            isPlanList
            hideTitle={planIndex !== 1}  // プラン追加時はタイトルを非表示
          />
        </div>
        {showSortList && (
          <div className="sortListWrapper">
            <SortList
              value={hotelsInput.sortOrder}
              handleChange={(sortOrder) => {
                dispatch(setSortOrder(sortOrder));

                if (!paginatorInfo) {
                  return;
                }

                const { currentPage } = paginatorInfo;
                const newhotelsInput = {
                  ..._hotelsInput,
                  sortOrder,
                };

                dispatch(
                  fetchHotels(
                    makeHotelsInput(newhotelsInput, flightsInput, planStartDate),
                    SHOWING_HOTEL_LIMIT,
                    currentPage
                  )
                );
              }}
            />
            {!!dataUpdateTime && (
              <DataUpdateTimeDisplay dataUpdateTime={dataUpdateTime} editingAccommodation={editingAccommodation} />
            )}
          </div>
        )}
        <div className="searchResultFacilityWrapper">
          <SearchResultFacility
            handlePlanSelect={handlePlanSelect}
            handleClickPlanDetailLink={(plan, roomTypeId) => {
              const planInput = makePlanInput(plan.id, roomTypeId, searchedHotelsInput, searchedFlightsInput, planStartDate);
              dispatch(fetchPendingPlan(planInput));
              scrollToTop(false);
              setHotelIdsWithExpandPlanList([]);
            }}
            hidePlanListLink={false}
            planStartDate={planStartDate}
            handleClickPlanListLink={(hotel) => {
              setHotelIdsWithExpandPlanList([...hotelIdsWithExpandPlanList, hotel.id]);
            }}
            hotelIdsWithExpandPlanList={hotelIdsWithExpandPlanList}
            fetchHotelsInput={makeHotelsInput(_hotelsInput, searchedFlightsInput, planStartDate)}
            editingAccommodation={editingAccommodation}
          />
        </div>
      </React.Fragment>
    );
  }

  const handlePlanChange = (planIndex, startDate, maxStayNights) => {
    dispatch(setStayNights(maxStayNights));
    setPlanStartDate(startDate);
    setMaxStayNights(maxStayNights);
    setPlanIndex(planIndex);
    toggleEditingAccommodation();
  };
  
  const handlePlanDelete = (startDate) => {
    const confirm = window.confirm("削除してよろしいですか？");
    if (confirm) {
      dispatch(handleDeletePlan(startDate));
    }
  };

  return (
    <React.Fragment>
      <div className="subscriptionTableContainer">
        <table style={{ tableLayout: 'fixed', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ width: 168 }}>利用日</th>
              <th style={{ width: 490 }}>旅程</th>
              <th style={{ width: 270 }}>内容変更</th>
            </tr>
          </thead>
          <tbody>
            {schedule && schedule.map((s, i) => {
              const dateDisplay = s.date.format('YYYY年M月D日');

              const outboundFlight = i === 0 ? selectedOutboundFlight : null;
              const inboundFlight = i === tripTotalDays - 1 ? selectedInboundFlight : null;

              const planIndex = i + 1;

              const { plan, planOptions } = s;

              const maxStayNights = getDaysToNextPlan(s.date, plans) || schedule.length - planIndex;
              const startDate = s.date.format('YYYY-MM-DD');

              return (
                <SubscriptionTableItem
                  key={planIndex}
                  index={planIndex}
                  outboundFlight={outboundFlight}
                  inboundFlight={inboundFlight}
                  dateDisplay={dateDisplay}
                  plan={plan}
                  planOptions={planOptions}
                  questionAndAnswers={questionAndAnswers}
                  onQuestionAnswerChange={onQuestionAnswerChange}
                  startDate={startDate}
                  handleFlightChange={toggleEditingFlights}
                  handlePlanChange={() => handlePlanChange(planIndex, startDate, maxStayNights)}
                  handlePlanDelete={() => handlePlanDelete(startDate)}
                  cantChangeHotel={!(plan && plan.firstDay)}
                  cantDeleteHotel={planIndex === 1}
                  handleUpdateCheckin={(checkin) => dispatch(handleUpdateCheckin(checkin, startDate))}
                  handleUpdateQuestion={(title, selected) => dispatch(handleUpdateQuestion(title, selected, startDate))}
                  showQuestions={!!(plan && plan.firstDay)}
                  hideMessage={!(plan && plan.firstDay)}
                  isLastDay={planIndex === tripTotalDays}
                  isFirstDay={planIndex === 1}
                  finalConfirm={false}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </React.Fragment>
  );
};

SubscriptionTable.defaultProps = {
};

SubscriptionTable.propTypes = {
  editingFlights: PropTypes.bool.isRequired,
  editingAccommodation: PropTypes.bool.isRequired,
  toggleEditingFlights: PropTypes.func.isRequired,
  toggleEditingAccommodation: PropTypes.func.isRequired,
  tripTotalDays: PropTypes.number.isRequired,
  schedule: PropTypes.array.isRequired,
  plans: PropTypes.array.isRequired,
  questionAndAnswers: PropTypes.array.isRequired,
  onQuestionAnswerChange: PropTypes.func.isRequired,
  maxStayNights: PropTypes.number,
  setMaxStayNights: PropTypes.func.isRequired,
  planStartDate: PropTypes.string,
  setPlanStartDate: PropTypes.func.isRequired,
  planEndDate: PropTypes.string,
  setPlanIndex: PropTypes.func.isRequired,
  planIndex: PropTypes.number,
};

export default SubscriptionTable;
